import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { usePolicyAcceptance } from './PolicyAcceptanceContext';
import { notificationService } from '../api/services/NotificationService';
import {
  startNotificationHub,
  stopNotificationHub,
  onReceiveNotification,
  offReceiveNotification,
} from '../api/signalr/notificationHub';
import type { RealTimeNotification } from '../api/types/notification';
import NotificationToastContainer, {
  type ToastItem,
} from '../components/Toast/NotificationToast';
import { NotificationTypeId } from '../utils/notificationMeta';

/** Unified notification shape used throughout the app. */
export interface Notification {
  id: string;
  typeId: number;
  title: string;
  message: string;
  isRead: boolean;
  createdAt?: string | null;
  /**
   * Free-form context bag from the backend (jsonb). Carries deep-link ids such as
   * `conversationId`, `listingId`, `soldListingId`, `sellerId` when relevant to
   * the notification type. See specs/008-messaging-context-ratings.
   */
  contextData?: Record<string, string> | null;
}

interface NotificationContextProps {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications yalnızca NotificationProvider içinde kullanılabilir.');
  }
  return context;
}

interface NotificationProviderProps {
  children: ReactNode;
}

function mapRealTime(rt: RealTimeNotification): Notification {
  return {
    id: rt.id,
    typeId: rt.typeId,
    title: rt.title,
    message: rt.message,
    isRead: false,
    createdAt: rt.createdAt,
    contextData: rt.contextData ?? null,
  };
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { openFromNotification } = usePolicyAcceptance();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const initialUnreadReceived = useRef(false);
  const hubStarted = useRef(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const result = await notificationService.getUnreadCounts();
      if (result.isSuccess && result.data != null) {
        setUnreadCount(result.data.general);
      }
    } catch {
      /* badge will show stale count – non-critical */
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const result = await notificationService.getAll('General');
      if (result.isSuccess && result.data) {
        const mapped: Notification[] = result.data.map((n) => ({
          id: n.id,
          typeId: n.notificationTypeId,
          title: n.title ?? n.notificationType?.title ?? '',
          message: n.message ?? n.notificationType?.message ?? '',
          isRead: n.isRead,
          createdAt: n.createdAt ?? null,
          contextData: n.contextData ?? null,
        }));

        setNotifications((prev) => {
          const realTimeById = new Map(
            prev.filter((p) => p.createdAt).map((p) => [p.id, p]),
          );
          return mapped.map((m) => realTimeById.get(m.id) ?? m);
        });
      }
    } catch {
      /* silent – page can show empty state */
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRealTimeNotification = useCallback(
    (rt: RealTimeNotification) => {
      // Message-category notifications are owned by MessageNotificationContext;
      // this context only tracks General notifications (feature 008 US3).
      if (rt.category !== 'General') return;

      const notif = mapRealTime(rt);

      setNotifications((prev) => {
        if (prev.some((n) => n.id === notif.id)) return prev;
        return [notif, ...prev];
      });

      if (initialUnreadReceived.current) {
        setUnreadCount((c) => c + 1);
        setToasts((prev) => [
          ...prev,
          {
            id: notif.id,
            typeId: notif.typeId,
            title: notif.title,
            message: notif.message,
            contextData: notif.contextData ?? null,
          },
        ]);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      initialUnreadReceived.current = false;
      if (hubStarted.current) {
        stopNotificationHub();
        hubStarted.current = false;
      }
      return;
    }

    fetchUnreadCount();

    let cancelled = false;

    async function connectHub() {
      try {
        initialUnreadReceived.current = false;
        onReceiveNotification(handleRealTimeNotification);
        await startNotificationHub();
        hubStarted.current = true;

        // After initial burst of unread notifications, mark flag so subsequent ones show toasts.
        setTimeout(() => {
          if (!cancelled) {
            initialUnreadReceived.current = true;
          }
        }, 2000);
      } catch (err) {
        console.error('Notification hub connection failed:', err);
      }
    }

    connectHub();

    return () => {
      cancelled = true;
      offReceiveNotification(handleRealTimeNotification);
      stopNotificationHub();
      hubStarted.current = false;
    };
  }, [isAuthenticated, fetchUnreadCount, handleRealTimeNotification]);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    notificationService.markAsRead(id).catch(() => {});
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    notificationService.markAllAsRead('General').catch(() => {});
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleToastClick = useCallback(
    (toast: ToastItem) => {
      if (toast.typeId === NotificationTypeId.RuleUpdate) {
        const opened = openFromNotification(
          {
            id: toast.id,
            typeId: toast.typeId,
            contextData: toast.contextData,
          },
          () => markAsRead(toast.id),
        );
        if (opened) return;
      }

      navigate('/notifications');
    },
    [markAsRead, navigate, openFromNotification],
  );

  const value: NotificationContextProps = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationToastContainer toasts={toasts} onDismiss={dismissToast} onToastClick={handleToastClick} />
    </NotificationContext.Provider>
  );
}

export default NotificationContext;
